import type Luminary from '@lumen5/luminary';
import type {
    Asset,
    AssetGroup,
    FancyCaptionAsset,
    IconAsset,
    MediaSourceData,
    Scene,
    SceneCategory,
    SceneGroup,
    TextAsset,
    TimeAwareMediaAsset,
} from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { NarrativeThreadEnum } from 'js/src/client';
import type { DASHBOARD_VIDEO_INIT_TYPE, OPTIONS_PAGE_VIDEO_INIT_TYPE } from 'js/src/data/app';
import { VIDEO_INIT_TYPE } from 'js/src/data/app';
import type { FlattenedAsset } from 'js/src/data/media';
import { isAudioSourceData } from 'js/src/data/media';
import type { MediaTransform, NormalizedRect, Size } from 'js/src/libs/crop-utils';
import { computeBaseMediaSize } from 'js/src/libs/crop-utils';
import type { ConfigurableElementProperties } from 'js/src/luminary';
import {
    getRootAssetGroup,
    getVoiceDrivenAssetPlaybackRegions,
    getVoiceDrivenAssetSourceData,
    getVoiceDrivenBaseAssetSourceDuration,
    isAIAvatarSceneGroup,
    isAIVoiceoverSceneGroup,
    isTalkingHeadSceneGroup,
    isVoiceDrivenSceneGroup,
    isVoiceoverSceneGroup,
    translateVideoTimeToTranscriptTime,
} from 'js/src/luminary';
import {
    getVoicedrivenSceneEndTime,
    getVoicedrivenSceneStartTime,
} from 'js/src/luminary/voicedriven-scenes';

// utility method to clamp a number between a min & max number
const _clamp = (num: number, min: number, max: number) => Math.min(Math.max(num, min), max);

/**
 * Resize all text assets on a scene so they fit as large a space as possible within the scene without being truncated.
 */
export function resizeTextToFitOnScene(scene: Scene): void {
    // check to ensure the scene's autoUpdate is true otherwise it's likely we're in a `batchUpdate()` loop which
    // means the text assets haven't had a chance to update their sizes making this call effectively a `noop`
    let turnAutoUpdateOff = false;
    if (!scene.autoUpdate) {
        // eslint-disable-next-line no-console
        console.warn(
            'Cannot resize text to fit on scene when scene.autoUpdate is false (turning it on temporarily)',
        );
        scene.autoUpdate = true;
        turnAutoUpdateOff = true;
    }

    scene
        .getAssetsByType(VideoData.TEXT_ASSET_TYPE)
        .filter((text) => text.isEditable)
        .forEach((textAsset) => {
            textAsset.setFontSizeToFitAllText();
        });

    if (turnAutoUpdateOff) {
        scene.autoUpdate = false;
    }
}

/**
 * Get text that will be used for media query suggestions. If it's a regular scene,
 * combine and return all text from it's scene & subscenes.
 * If it's a voice-driven scene, use the captions within the scene.
 */
export function getSceneTextForMediaQuery(scene: Scene): string {
    let aggregatedText = '';
    const sceneGroup = scene.sceneGroup!;
    const captions = sceneGroup.captions;
    if (isVoiceDrivenSceneGroup(sceneGroup) && captions) {
        const playbackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
        const baseAssetSourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
        const sceneStartTime = translateVideoTimeToTranscriptTime(
            getVoicedrivenSceneStartTime(scene),
            playbackRegions,
            baseAssetSourceDuration,
            sceneGroup.startTime,
        );
        const sceneEndTime = translateVideoTimeToTranscriptTime(
            getVoicedrivenSceneEndTime(scene),
            playbackRegions,
            baseAssetSourceDuration,
            sceneGroup.startTime,
            {
                includeTrailingCut: false,
            },
        );
        let startIndex = 0;
        let endIndex = 0;
        const transcriptWords = captions.asset.transcript.words;
        // Get and aggregate all caption text that fall within the scene duration
        for (let i = 0; i < transcriptWords.length; i++) {
            const word = transcriptWords[i];
            if (word.endTime > sceneStartTime && word.startTime < sceneEndTime) {
                if (!aggregatedText) {
                    startIndex = i;
                }
                endIndex = i;
                aggregatedText = aggregatedText + ' ' + word.word.trim();
            }
        }
        aggregatedText = aggregatedText.trim();
        // We want to try and return full sentences so we check the previous and next caption blocks to see if we can terminate at a sentence break.
        const sentenceBreaks = ['.', '?', '!'];
        let currentIndex = endIndex + 1;
        // Only check next block if current block doesn't already terminate on a sentence break.
        if (
            currentIndex < transcriptWords.length &&
            (!aggregatedText ||
                !sentenceBreaks.includes(aggregatedText.charAt(aggregatedText.length - 1)))
        ) {
            const text = transcriptWords[currentIndex].word.trim();
            let i = 0;
            // Loop forward through each character in next text block and add text until we find a sentence break, otherwise add all text.
            while (i < text.length) {
                if (sentenceBreaks.includes(text.charAt(i))) {
                    i += 1;
                    break;
                }
                i += 1;
            }
            aggregatedText = aggregatedText + ' ' + text.substring(0, i).trim();
        }
        currentIndex = startIndex - 1;
        const previousText = currentIndex >= 0 ? transcriptWords[currentIndex].word.trim() : '';
        // Only check previous block if current block isn't a new sentence. i.e if previous text doesn't terminate at a sentence break
        if (
            previousText &&
            !sentenceBreaks.includes(previousText.charAt(aggregatedText.length - 1))
        ) {
            const text = transcriptWords[currentIndex].word.trim();
            let i = text.length - 1;
            // Loop backward and add text in previous blocks until we find a sentence break
            while (i >= 0) {
                if (sentenceBreaks.includes(text.charAt(i))) {
                    break;
                }
                i -= 1;
            }
            aggregatedText = text.substring(i + 1, text.length).trim() + ' ' + aggregatedText;
        }
    } else {
        scene
            .getAssetsByType(VideoData.TEXT_ASSET_TYPE)
            .filter((text) => text.isEditable)
            .forEach((textAsset) => {
                aggregatedText += textAsset.text.trim() + ' ';
            });
    }
    return aggregatedText.trim();
}

/**
 * Gets the max position where we can add a scene group, considering that we can't add after an outro
 * or after a branding scene.
 */
export function getMaxSceneGroupTargetPosition(luminary: Luminary) {
    const hasOutroScene = luminary.getOutroSceneGroup() !== undefined;
    const hasBrandingScene =
        luminary.scenes.find((scene) => scene.sceneCategory === VideoData.CATEGORY_BRAND) !==
        undefined;
    const maxIndex =
        luminary.sceneGroups.length - 1 - (hasOutroScene ? 1 : 0) - (hasBrandingScene ? 1 : 0);

    return maxIndex;
}

/**
 * Gets the correct targetIndex for a specified targetIndex, correcting to makes ure it's within the video
 */
export function getTargetIndex(luminary: Luminary, targetIndex: number | null) {
    const maxTargetIndex = getMaxSceneGroupTargetPosition(luminary);
    if (targetIndex === null) {
        return maxTargetIndex;
    }
    if (targetIndex < 0) {
        return 0;
    }
    return Math.min(targetIndex, maxTargetIndex);
}

export type NormalizedAssetBounds = {
    normalizedWidth: number;
    normalizedHeight: number;
    normalizedX: number;
    normalizedY: number;
};

/**
 * `approximateMaxBounds` on a group child collapses every child onto the group's transformation
 * y, so callers that need the asset's actual stacked layout y go through this helper instead of
 * reading `approximateMaxBounds` directly. Mirrors the bounds-selection used by
 * `getNormalizedAssetBounds` and `text-contrast-utils`.
 */
export function getEffectiveAssetBounds(asset: TextAsset | FancyCaptionAsset | AssetGroup): {
    x: number;
    y: number;
    width: number;
    height: number;
} {
    const isHeightConstrainedToMaxBounds =
        asset.hasUserChangedMaxHeight || (VideoData.isTextAsset(asset) && asset.isClipping);
    if (!asset.hasUserChangedMaxWidth && !isHeightConstrainedToMaxBounds) {
        return asset.boundsWithoutAnimation;
    }
    const renderedBounds = asset.boundsWithoutAnimation;
    const maxBounds = asset.approximateMaxBounds;
    const useMaxY = isHeightConstrainedToMaxBounds && !asset.isPositionControlledByParent;
    // AssetGroup.approximateMaxBounds.height clamps to max(content_bounds, maxHeight). For an
    // AssetGroup where the user has shrunk maxHeight below content, that clamp anchors the editor
    // outline to the content height instead of the user's drag. maxHeight and the projected y
    // below keep the outline aligned with the actual maxHeight box.
    const heightSource = (() => {
        if (!isHeightConstrainedToMaxBounds) return renderedBounds.height;
        if (VideoData.isAssetGroup(asset)) return asset.maxHeight;
        return maxBounds.height;
    })();
    const ySource = (() => {
        if (!useMaxY) return renderedBounds.y;
        if (VideoData.isAssetGroup(asset)) {
            return (
                asset.transformation.position.y - asset.transformation.anchor.y * asset.maxHeight
            );
        }
        return maxBounds.y;
    })();
    return {
        x: asset.hasUserChangedMaxWidth ? maxBounds.x : renderedBounds.x,
        y: ySource,
        width: asset.hasUserChangedMaxWidth ? maxBounds.width : renderedBounds.width,
        height: heightSource,
    };
}

/**
 * Returns the bounds (x, y, width, height) for a Luminary asset normalized as a percentage (e.g. 0.5).
 * For assets within an asset group, the bounds are relative to the asset group's bounds, while
 * assets not in a group are relative to the scene's bounds. Pass `relativeToScene` for an overlay
 * that is mounted on the scene rather than inside the asset group's element.
 */
export function getNormalizedAssetBounds(
    asset: Asset | null | undefined,
    { relativeToScene = false }: { relativeToScene?: boolean } = {},
): NormalizedAssetBounds & { leftOutOfBounds: boolean; rightOutOfBounds: boolean } {
    if (!asset) {
        return {
            normalizedWidth: -1,
            normalizedHeight: -1,
            normalizedX: -1,
            normalizedY: -1,
            leftOutOfBounds: true,
            rightOutOfBounds: true,
        };
    }

    const {
        x: xCoord,
        y: yCoord,
        width,
        height,
    } = (() => {
        if (
            VideoData.isTextAsset(asset) ||
            VideoData.isFancyCaptionAsset(asset) ||
            VideoData.isAssetGroup(asset)
        ) {
            return getEffectiveAssetBounds(asset);
        }

        if (asset.isPositionControlledByParent) {
            return asset.boundsWithoutAnimation;
        }

        return asset.approximateMaxBounds; // TODO: better bounds calculation! (tricky w/ masks)
    })();

    const rootAssetGroup = relativeToScene ? null : getRootAssetGroup(asset);

    const {
        relativeWidth,
        relativeHeight,
        relativeX: initialRelativeX,
        relativeY: initialRelativeY,
    } = (() => {
        if (rootAssetGroup) {
            // Children must normalize against the same frame the AG div itself uses, or their
            // divs drift relative to where pixi renders.
            const reference = rootAssetGroup.hasUserChangedMaxHeight
                ? getEffectiveAssetBounds(rootAssetGroup)
                : rootAssetGroup.boundsWithoutAnimation;
            return {
                relativeWidth: reference.width,
                relativeHeight: reference.height,
                relativeX: xCoord - reference.x,
                relativeY: yCoord - reference.y,
            };
        }
        return {
            relativeWidth: asset.root.referenceResolution.width,
            relativeHeight: asset.root.referenceResolution.height,
            relativeX: xCoord,
            relativeY: yCoord,
        };
    })();

    const {
        containerWidth,
        containerHeight,
        relativeX,
        relativeY,
        leftOutOfBounds,
        rightOutOfBounds,
    } = (() => {
        let w = width;
        let x = initialRelativeX;

        const leftOutOfBounds = x < 0;
        if (leftOutOfBounds) {
            w += x;
            x = Math.max(0, x);
        }

        let h = height;
        let y = initialRelativeY;

        const topOutOfBounds = y < 0;
        if (topOutOfBounds) {
            h += y;
            y = Math.max(0, y);
        }

        const rightOutOfBounds = x + w > relativeWidth;
        if (rightOutOfBounds) {
            w -= x + w - relativeWidth;
        }

        const bottomOutOfBounds = y + h > relativeHeight;
        if (bottomOutOfBounds) {
            h -= y + h - relativeHeight;
        }

        return {
            containerWidth: w,
            containerHeight: h,
            relativeX: x,
            relativeY: y,
            leftOutOfBounds,
            rightOutOfBounds,
        };
    })();

    return {
        normalizedWidth: _clamp(containerWidth / relativeWidth, 0, 1),
        normalizedHeight: _clamp(containerHeight / relativeHeight, 0, 1),
        normalizedX: _clamp(relativeX / relativeWidth, 0, 1),
        normalizedY: _clamp(relativeY / relativeHeight, 0, 1),
        leftOutOfBounds,
        rightOutOfBounds,
    };
}

/**
 * The transform a crop/transform session edits, read off the asset. Null for assets that have no
 * such transform (anything that isn't media or an icon).
 */
export function getMediaTransform(asset: Asset | null | undefined): MediaTransform | null {
    if (!asset || !(VideoData.isMediaAsset(asset) || VideoData.isIconAsset(asset))) {
        return null;
    }
    return {
        xTranslation: asset.xTranslation,
        yTranslation: asset.yTranslation,
        scaleX: asset.scaleX,
        rotation: asset.transformation.angle,
        flipHorizontally: asset.flipHorizontally,
        flipVertically: asset.flipVertically,
    };
}

/**
 * The geometry a transform gizmo needs to draw itself over an asset: the asset's normalized rect, and
 * (for media only) the fixed frame it fills plus the media's 1x fit size within that frame. Unlike
 * `getMediaTransform` this is independent of the user's zoom/pan, so it stays stable across a drag.
 */
export function getMediaTransformGeometry(asset: Asset | null | undefined): {
    bounds: NormalizedRect;
    viewport: Size | null;
    baseMedia: Size | null;
} | null {
    if (!asset || !(VideoData.isMediaAsset(asset) || VideoData.isIconAsset(asset))) {
        return null;
    }
    const bounds = getNormalizedAssetBounds(asset, { relativeToScene: true });
    const boundsRect = {
        x: bounds.normalizedX,
        y: bounds.normalizedY,
        width: bounds.normalizedWidth,
        height: bounds.normalizedHeight,
    };
    if (!VideoData.isMediaAsset(asset)) {
        return { bounds: boundsRect, viewport: null, baseMedia: null };
    }
    const viewport = {
        width: asset.mediaTargetResolution.width,
        height: asset.mediaTargetResolution.height,
    };
    return {
        bounds: boundsRect,
        viewport,
        baseMedia: computeBaseMediaSize(
            asset.sourceData ?? null,
            viewport,
            asset.fillMode === VideoData.FILL_CONTAIN,
        ),
    };
}

/**
 * Returns true if sceneCategory is voice-driven
 */
export function isVoiceDrivenCompatibleSceneCategory(sceneCategory: SceneCategory): boolean {
    if (sceneCategory === VideoData.CATEGORY_COMMON) {
        return true;
    }
    if (sceneCategory === VideoData.CATEGORY_TALKING_HEAD_INSERT) {
        return true;
    }
    if (sceneCategory === VideoData.CATEGORY_VOICEOVER) {
        return true;
    }
    if (sceneCategory === VideoData.CATEGORY_TALKING_HEAD) {
        return true;
    }
    return false;
}

/**
 * Please see {@link https://stackoverflow.com/questions/588004/is-floating-point-math-broken} first
 * Floating point subtraction with a tolerance level of 3 decimal places. For comparison, please @see floatingPointComparison
 * @param floatA - the floatA in floatA - floatB
 * @param floatB - floatB in floatA - floatB
 */
export function floatingPointSubtraction(floatA: number, floatB: number): number {
    const tolerance = 3;
    return Number.parseFloat((floatA - floatB).toFixed(tolerance));
}

/**
 * Function to compare floating points with a lenient tolerance (0.001). For comparison we're more lenient
 * than the subtraction operation because comparisons are usually used to call add/remove scenes, where as
 * arithmetic operations are used to set durations.
 * @see floatingPointSubtraction for floating point stack overflow post
 * @param floatA - a number
 * @param floatB - a number
 * @returns whether the two numbers are equal within tolerance level of 0.001
 */
export function floatingPointComparison(floatA: number, floatB: number): boolean {
    const tolerance = 0.001; // kind of arbitrary but essentially ~ toFixed(6)
    return Math.abs(floatA - floatB) < tolerance;
}

export function getNarrativeThreadFromVideoInitType(
    videoInitType: DASHBOARD_VIDEO_INIT_TYPE | OPTIONS_PAGE_VIDEO_INIT_TYPE,
): NarrativeThreadEnum {
    switch (videoInitType) {
        case VIDEO_INIT_TYPE.CREATE_FROM_SCRATCH:
            return NarrativeThreadEnum.TEXT; // defaults to a text scene
        case VIDEO_INIT_TYPE.VOICEOVER:
            return NarrativeThreadEnum.VOICEOVER;
        case VIDEO_INIT_TYPE.TALKING_HEAD:
            return NarrativeThreadEnum.TALKING_HEAD;
        case VIDEO_INIT_TYPE.TEXT_TO_VIDEO:
            return NarrativeThreadEnum.TEXT;
        case VIDEO_INIT_TYPE.TEXT_TO_AI_VOICEOVER:
            return NarrativeThreadEnum.VOICEOVER;
        case VIDEO_INIT_TYPE.TEXT_TO_AI_AVATAR:
            return NarrativeThreadEnum.TALKING_HEAD;
        default:
            throw new Error('Invalid video init type!');
    }
}

/**
 * Function that returns the VideoInitType for a given NarrativeThread
 * This is the reverse of getNarrativeThreadFromVideoInitType
 */
export function getVideoInitTypeFromNarrativeThread(
    narrativeThread: NarrativeThreadEnum,
): OPTIONS_PAGE_VIDEO_INIT_TYPE {
    switch (narrativeThread) {
        case NarrativeThreadEnum.TEXT:
            return VIDEO_INIT_TYPE.TEXT_TO_VIDEO;
        case NarrativeThreadEnum.VOICEOVER:
            return VIDEO_INIT_TYPE.TEXT_TO_AI_VOICEOVER;
        case NarrativeThreadEnum.TALKING_HEAD:
            return VIDEO_INIT_TYPE.TEXT_TO_AI_AVATAR;
        default:
            throw new Error('Invalid narrative thread!');
    }
}

/**
 * Function that returns the NarrativeThread for a given SceneGroup
 */
export function getNarrativeThreadForSceneGroup(sceneGroup: SceneGroup): NarrativeThreadEnum {
    if (isVoiceoverSceneGroup(sceneGroup)) {
        return NarrativeThreadEnum.VOICEOVER;
    }
    if (isTalkingHeadSceneGroup(sceneGroup)) {
        return NarrativeThreadEnum.TALKING_HEAD;
    }
    return NarrativeThreadEnum.TEXT;
}

export function setVoiceDrivenBaseAssetEndTime(sceneGroup: SceneGroup, endTimeMillis: number) {
    if (isTalkingHeadSceneGroup(sceneGroup)) {
        sceneGroup.mediaAsset.endTime = endTimeMillis;
    } else if (isVoiceoverSceneGroup(sceneGroup)) {
        sceneGroup.voiceoverAsset.endTime = endTimeMillis;
    }
}

export function setVoiceDrivenBaseAssetStartTime(sceneGroup: SceneGroup, startTimeMillis: number) {
    if (isTalkingHeadSceneGroup(sceneGroup)) {
        sceneGroup.mediaAsset.startTime = startTimeMillis;
    } else if (isVoiceoverSceneGroup(sceneGroup)) {
        sceneGroup.voiceoverAsset.startTime = startTimeMillis;
    }
}

export function hasExceededVideoDurationLimit(
    luminary: Readonly<Luminary>,
    videoDurationLimitSeconds: number | null,
): boolean {
    if (!videoDurationLimitSeconds) {
        return false;
    }
    return luminary.duration > videoDurationLimitSeconds * 1000;
}

export function getCurrentAIVOSecondsUsed(luminary: Readonly<Luminary>): number {
    return Math.floor(
        luminary.sceneGroups.reduce((duration, sceneGroup) => {
            if (isAIVoiceoverSceneGroup(sceneGroup) && !isAIAvatarSceneGroup(sceneGroup)) {
                duration += sceneGroup.duration;
            }
            return duration;
        }, 0) / 1000,
    );
}

export function resizeTextToFitOnSceneForEntireVideo(luminary: Luminary): void {
    luminary.scenes.forEach((scene: Scene) => resizeTextToFitOnScene(scene));
}

/**
 * Remove all the scenes & scene groups from the video
 */
export function clearScenes(luminary: Luminary, sceneGroupIdsToPreserve: string[]) {
    luminary.batchRun(() => {
        // clear old deprecated voiceover track if it exists
        luminary.voiceover = null;

        // remove all scene groups except those in sceneGroupIdsToPreserve
        const sceneGroupIds = luminary.sceneGroups.map((sceneGroup) => sceneGroup.id);
        const sceneGroupIdsToDelete = sceneGroupIds.filter(
            (sceneGroupId) => !sceneGroupIdsToPreserve.includes(sceneGroupId),
        );

        // we want to make sure that we don't delete all scenes (since that'll leave the Luminary object in
        // an invalid state for saving/previewing/publishing). So error if we're about to delete all scenes.
        if (sceneGroupIdsToDelete.length === sceneGroupIds.length) {
            throw new Error('Cannot delete all scenes');
        }

        sceneGroupIdsToDelete.forEach((sceneGroupId) => {
            luminary.removeSceneGroup(sceneGroupId);
        });
    });
}

export function clearScenesExceptCategories({
    luminary,
    sceneGroupIdsToPreserve,
    preserveOutroScenes = false,
}: {
    luminary: Luminary;
    sceneGroupIdsToPreserve: string[];
    preserveOutroScenes?: boolean;
}) {
    const sceneIdsToPreserveCategories = luminary.scenes
        .filter((scene) => {
            const isOutroScene = scene.sceneCategory === VideoData.CATEGORY_OUTRO;
            return preserveOutroScenes && isOutroScene;
        })
        .map((scene) => scene.id);
    // get list of scene group ids matching the categories we want to preserve
    const sceneIdsToPreserveSet = new Set(sceneIdsToPreserveCategories);
    const sceneGroupIdsToPreserveCategories = luminary.sceneGroups
        .filter((sceneGroup) => {
            return sceneGroup.scenes.some((scene) => sceneIdsToPreserveSet.has(scene.id));
        })
        .map((sceneGroup) => sceneGroup.id);

    const allSceneGroupIdsToPreserve = [
        ...sceneGroupIdsToPreserve,
        ...sceneGroupIdsToPreserveCategories,
    ];

    clearScenes(luminary, allSceneGroupIdsToPreserve);
}

const SCREEN_RECORDING_SUFFIX = '#screenRecording';

export function findScreenRecordingMediaAssetsInScene(scene: Scene): TimeAwareMediaAsset[] {
    const mediaAssets = scene
        .getAssets()
        .filter(VideoData.isMediaAsset)
        .filter((asset) => isScreenRecordingSourceData(asset.sourceData));
    return mediaAssets;
}

export function isScreenRecordingScene(scene: Scene): boolean {
    const mediaAssets = findScreenRecordingMediaAssetsInScene(scene);
    return mediaAssets.length > 0;
}

export function isScreenRecordingSceneGroup(sceneGroup: SceneGroup): boolean {
    if (!isVoiceDrivenSceneGroup(sceneGroup)) {
        return false;
    }
    const sourceData = getVoiceDrivenAssetSourceData(sceneGroup);
    if (isAudioSourceData(sourceData)) {
        return false;
    }
    return isScreenRecordingSourceData(sourceData);
}

export function isScreenRecordingSourceData(
    sourceData: MediaSourceData | FlattenedAsset | null | undefined,
): boolean {
    return hasScreenRecordingSuffix(sourceData?.source_id || '');
}

export function appendScreenRecordingSuffix(sourceId: string): string {
    return `${sourceId}${SCREEN_RECORDING_SUFFIX}`;
}

export function hasScreenRecordingSuffix(sourceId: string): boolean {
    return sourceId.endsWith(SCREEN_RECORDING_SUFFIX);
}

export function getMediaAssetSnapshot(
    luminary: Luminary,
    sceneIndices: number[],
): Map<string, string | null> {
    const snapshot = new Map<string, string | null>();

    sceneIndices.forEach((sceneIndex) => {
        const scene = luminary.scenes[sceneIndex];
        scene
            .getAssetsByType(VideoData.MEDIA_ASSET_TYPE)
            .filter((asset) => asset.isEditable)
            .forEach((asset) => {
                snapshot.set(`${sceneIndex}-${asset.name}`, asset.sourceData?.uuid || null);
            });
    });
    return snapshot;
}

/**
 * Checks if the outro scene group has any video media with volume enabled.
 * If so, sets the outro scene group's musicVolumeOverride to 0.
 * This prevents background music from conflicting with outro video audio.
 * @returns true if musicVolumeOverride was changed, false otherwise
 */
export function muteOutroMusicIfHasMediaWithVolume(luminary: Luminary): boolean {
    const outroSceneGroup = luminary.getOutroSceneGroup();
    if (!outroSceneGroup) {
        return false;
    }

    const sceneGroup = outroSceneGroup.sceneGroup;
    let hasMediaWithVolume = false;

    for (const scene of sceneGroup.scenes) {
        const mediaAssets = scene.getAssetsByType(VideoData.MEDIA_ASSET_TYPE);
        for (const asset of mediaAssets) {
            if (asset.isVideo && asset.volume > 0) {
                hasMediaWithVolume = true;
                break;
            }
        }
        if (hasMediaWithVolume) {
            break;
        }
    }

    if (hasMediaWithVolume && sceneGroup.musicVolumeOverride !== 0) {
        sceneGroup.musicVolumeOverride = 0;
        return true;
    }

    return false;
}

export function getAssetCenterXY(asset: Asset) {
    const anchor = asset.transformation.anchor;
    if (anchor.x === 0.5 && anchor.y === 0.5) {
        return {
            x: asset.transformation.position.x,
            y: asset.transformation.position.y,
        };
    }
    const bounds = asset.approximateMaxBounds;
    return {
        x: bounds.x + bounds.width * 0.5,
        y: bounds.y + bounds.height * 0.5,
    };
}

export function getElementPropertiesToSave(
    asset: TimeAwareMediaAsset | IconAsset,
    sceneDuration: number,
): ConfigurableElementProperties {
    const position = getAssetCenterXY(asset);
    const dimensions = {
        height: asset.maxHeight,
        width: asset.maxWidth,
    };
    if (asset.parentId) {
        dimensions.height = asset.height;
        dimensions.width = asset.width;
    }
    const introOutroTimes = {
        introTime: asset.introAnimationOffset,
        exitTime: sceneDuration - asset.outroAnimationOffset,
    };
    const dynamicStyles = asset.hasDynamicStyle ? asset.saveDynamicStyle() : null;
    let groupIndex = null;
    const assetGroup = asset.getParentByType(VideoData.GROUP_ASSET_TYPE);
    if (assetGroup) {
        groupIndex = assetGroup.getAssetIndex(asset);
    }
    return {
        position,
        dimensions,
        introOutroTimes,
        dynamicStyles,
        groupIndex,
    };
}

export function isTextSticker(asset: Asset): boolean {
    if (!VideoData.isStickerAsset(asset)) return false;
    if (VideoData.isTextAsset(asset)) return true;
    if (VideoData.isAssetGroup(asset)) {
        return asset.getAssets().some(VideoData.isTextAsset);
    }
    return false;
}
